import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarClock, Upload, FileUp, ListChecks } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { addDays } from '../../utils/payPeriod';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';
import { XdockRecheckPanel } from '../../components/nursery/retail/XdockRecheckPanel';
import { XdockCutoffSetting } from '../../components/nursery/retail/XdockCutoffSetting';
import { XDOCK_DAY_NAMES, type XdockFilePreview, type XdockSchedule } from '../../types/nurseryRetail';

/**
 * /nursery/cross-dock — the retailer's cross dock pick lists.
 *
 * The retailer sends a sheet whenever pick days change: a standing one, and
 * one-week sheets for public holidays. Uploading reads the sheet and shows
 * what was found before anything is saved. A purchase order that asks for a
 * day its store is not picked is moved forward to the next day it is.
 *
 * docs/retailer-po-and-cross-dock-plan.md
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';

/** A file's bytes as base64, for a JSON request. */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('The file could not be read'));
    reader.readAsDataURL(file);
  });
}

interface SaveForm {
  name: string;
  is_default: boolean;
  commence_date: string;
  end_date: string;
}

function CrossDockSchedules() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [schedules, setSchedules] = useState<XdockSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<XdockFilePreview | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [form, setForm] = useState<SaveForm | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  // Bumped whenever the schedules change, so the wrong-day list is read again.
  const [changes, setChanges] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nurseryRetailService.getSchedules();
      if (res.success) setSchedules(res.data || []);
      else setError(errorMessage(res, 'Could not load the schedules'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the schedules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const formFor = (p: XdockFilePreview, idx: number): SaveForm => {
    const meta = p.sheets[idx].meta;
    return {
      name: meta.name || p.file_name.replace(/\.[^.]+$/, ''),
      // A sheet that says when it commences is for that week. One that does
      // not is the standing schedule.
      is_default: !meta.commence_date,
      commence_date: meta.commence_date ?? '',
      end_date: meta.commence_date ? addDays(meta.commence_date, 6) : '',
    };
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await nurseryRetailService.previewSchedule(file.name, await readBase64(file));
      if (res.success && res.data) {
        setPreview(res.data);
        setSheetIdx(0);
        setForm(formFor(res.data, 0));
      } else setError(errorMessage(res, 'Could not read that file'));
    } catch (e) {
      setError(errorMessage(e, 'Could not read that file'));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const chooseSheet = (idx: number) => {
    if (!preview) return;
    setSheetIdx(idx);
    setForm(formFor(preview, idx));
  };

  const cancelPreview = () => {
    setPreview(null);
    setForm(null);
  };

  const save = async () => {
    if (!preview || !form) return;
    const sheet = preview.sheets[sheetIdx];
    if (!form.is_default && !form.commence_date) {
      setError('Give the schedule a first day, or make it the standing schedule.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryRetailService.saveSchedule({
        name: form.name.trim() || 'Cross dock schedule',
        is_default: form.is_default,
        commence_date: form.is_default ? null : form.commence_date,
        end_date: form.is_default ? null : (form.end_date || null),
        updated_note: sheet.meta.updated_note || null,
        file_name: preview.file_name,
        sheet_name: sheet.sheet_name,
        rows: sheet.rows,
      });
      if (res.success) {
        cancelPreview();
        setMessage(`${res.data.name} saved.`);
        setChanges((n) => n + 1);
        await load();
      } else setError(errorMessage(res, 'Could not save the schedule'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the schedule'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      setMessage(done);
      setRemoving(null);
      setChanges((n) => n + 1);
      await load();
    } catch (e) {
      setError(errorMessage(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  if (licenseLoading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what="Cross dock schedules are kept" />;

  const sheet = preview ? preview.sheets[sheetIdx] : null;

  return (
    <>
      <PageHeader
        title="Cross dock schedules"
        subtitle="Which day each store's order is made up for the cross dock."
        icon={<CalendarClock className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/orders/import')}>
              <FileUp className="h-4 w-4" /> Import purchase orders
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/retailer-range')}>
              <ListChecks className="h-4 w-4" /> Range upload
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {message && <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{message}</div>}

        <XdockRecheckPanel refreshKey={changes} />

        {!sheet || !form ? (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold text-neutral-800">Upload a schedule</h2>
            <p className="text-sm text-neutral-600">
              Upload the pick list as the retailer sent it (.xlsx), or saved as CSV. You see what was read before anything is saved.
            </p>
            <input
              ref={fileInput}
              id="xdock-file"
              type="file"
              accept=".xlsx,.xlsm,.csv"
              className="sr-only"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
              <Upload className="h-4 w-4" /> {busy ? 'Reading…' : 'Choose the pick list'}
            </Button>
          </section>
        ) : (
          <section aria-label="Schedule read from the file" className="bg-white border border-primary-200 rounded-xl p-4 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-neutral-800">Check this looks right, then save</h2>
              <p className="text-xs text-neutral-500 break-words">{preview!.file_name}</p>
            </div>

            {preview!.sheets.length > 1 && (
              <div>
                <label htmlFor="xdock-sheet" className="block text-sm font-medium text-neutral-700 mb-1">
                  This workbook has {preview!.sheets.length} schedules. Which one?
                </label>
                <select id="xdock-sheet" className={inputCls} value={sheetIdx} onChange={(e) => chooseSheet(Number(e.target.value))}>
                  {preview!.sheets.map((s, i) => (
                    <option key={i} value={i}>{s.sheet_name || s.meta.name || `Sheet ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            )}

            <dl className="grid grid-cols-3 gap-2 text-center">
              {[
                ['Store rows', sheet.summary.rows],
                ['Stores', sheet.summary.stores],
                ['Pick days', sheet.meta.day_columns],
              ].map(([label, value]) => (
                <div key={label} className="bg-neutral-50 rounded-lg px-2 py-2">
                  <dd className="text-lg font-semibold text-neutral-900">{value}</dd>
                  <dt className="text-xs text-neutral-500">{label}</dt>
                </div>
              ))}
            </dl>

            <ul className="text-sm divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
              {XDOCK_DAY_NAMES.map((day, d) => (
                <li key={day} className="flex justify-between gap-3 px-3 py-1.5">
                  <span className="text-neutral-800">{day}</span>
                  <span className={sheet.summary.per_day[d] ? 'text-neutral-800' : 'text-neutral-400'}>
                    {sheet.summary.per_day[d] ? `${sheet.summary.per_day[d]} stores` : 'nothing picked'}
                  </span>
                </li>
              ))}
            </ul>

            <div className="text-sm text-neutral-700">
              <p>
                {sheet.stores.matched} of {sheet.summary.stores} stores on the sheet are customers of yours.
                {sheet.stores.unmatched.length > 0 && ' The rest need nothing unless you supply them.'}
              </p>
              {sheet.stores.unmatched.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-primary-700">
                    {sheet.stores.unmatched.length} store{sheet.stores.unmatched.length === 1 ? '' : 's'} with no customer
                  </summary>
                  <p className="mt-1 text-xs text-neutral-500">
                    A purchase order for one of these cannot be imported until the store is a customer with its store number.
                  </p>
                  <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-neutral-600 space-y-0.5">
                    {sheet.stores.unmatched.map((s) => (
                      <li key={s.store_code} className="break-words">{s.store_code} {s.store_name}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="xdock-name" className="block text-sm font-medium text-neutral-700 mb-1">Schedule name</label>
                <input id="xdock-name" className={inputCls} maxLength={150} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>

              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input type="checkbox" className="mt-0.5 h-4 w-4 flex-shrink-0" checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                <span>
                  Standing schedule
                  <span className="block text-xs text-neutral-500">
                    Used for any date no dated schedule covers. It replaces the standing schedule in force now.
                  </span>
                </span>
              </label>

              {!form.is_default && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="xdock-from" className="block text-sm font-medium text-neutral-700 mb-1">First day it applies</label>
                    <input id="xdock-from" type="date" className={inputCls} value={form.commence_date}
                      onChange={(e) => setForm({
                        ...form,
                        commence_date: e.target.value,
                        end_date: e.target.value ? addDays(e.target.value, 6) : form.end_date,
                      })} />
                  </div>
                  <div>
                    <label htmlFor="xdock-to" className="block text-sm font-medium text-neutral-700 mb-1">Last day it applies</label>
                    <input id="xdock-to" type="date" className={inputCls} value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    <p className="text-xs text-neutral-500 mt-1">Left blank, it ends six days after it starts.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save schedule'}</Button>
              <Button size="sm" variant="outline" onClick={cancelPreview} disabled={busy}>Cancel</Button>
            </div>
          </section>
        )}

        <XdockCutoffSetting />

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-neutral-800">Schedules in use</h2>
          {loading ? (
            <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />
          ) : schedules.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-6 text-sm text-neutral-600">
              No schedule has been uploaded yet. Upload the standing sheet first and save it as the standing schedule, then add each
              special week as the retailer sends it. Until then a purchase order keeps the date it asks for.
            </div>
          ) : (
            <ul className="space-y-3">
              {schedules.map((s) => (
                <li key={s.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Link to={`/nursery/cross-dock/${s.id}`} className="font-semibold text-primary-700 hover:underline break-words min-w-0">
                      {s.name}
                    </Link>
                    {s.is_default && (
                      <span className="rounded-full bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5">Standing</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 break-words">
                    {s.is_default || !s.commence_date
                      ? 'Any date no dated schedule covers'
                      : `${formatLocalDateInTz(s.commence_date, tz)} to ${s.end_date ? formatLocalDateInTz(s.end_date, tz) : ''}`}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-500 break-words">
                    {s.store_count} stores · uploaded {formatDateInTz(s.created_at, tz)}
                    {s.created_by_name ? ` by ${s.created_by_name}` : ''}
                    {s.updated_note ? ` · sheet ${s.updated_note}` : ''}
                  </p>
                  {removing === s.id ? (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 space-y-2">
                      <p>
                        Remove {s.name}?{' '}
                        {s.is_default
                          ? 'With no standing schedule, a purchase order outside a dated week keeps the date it asks for.'
                          : 'Its dates go back to the standing schedule.'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="danger" disabled={busy}
                          onClick={() => act(() => nurseryRetailService.removeSchedule(s.id), `${s.name} removed.`)}>
                          Remove
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setRemoving(null)}>Keep it</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/nursery/cross-dock/${s.id}`)}>Stores and tester</Button>
                      {!s.is_default && (
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => nurseryRetailService.makeDefault(s.id), `${s.name} is now the standing schedule.`)}>
                          Make standing
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRemoving(s.id)}>Remove</Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-xs text-neutral-500">
          The sheet is a supplier pick list: the day shown is the day the order is made up and sent to the dock, not the day it reaches
          the store. The cut-off times in a store's route are shown and never applied; the same-day cut-off above is.
        </p>
      </div>
    </>
  );
}

export default CrossDockSchedules;
